import {
  DragDropContext,
  Draggable,
  Droppable,
  type OnDragEndResponder,
} from "@hello-pangea/dnd";
import { CircleAlert, Eye, EyeOff, Flame, Plus } from "lucide-react";
import { useDataProvider, useGetList, useStore, useTranslate } from "ra-core";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";

import { CampanaAvisos } from "../equipo/CampanaAvisos";
import type { NevoraDataProvider } from "../datos/dataProvider";
import { useEsAdmin } from "../datos/useEsAdmin";
import { useGestiona } from "../datos/useMiRol";
import { useResponsables } from "../datos/useResponsables";
import { filtrarPorPersonas } from "../dominio/equipo";
import { FiltroPersonasSelect } from "../equipo/FiltroPersonasSelect";
import { useFiltroPersonas } from "../equipo/useFiltroPersonas";
import { proximaAccion } from "../dominio/agenda";
import { textoDatosQueFaltan } from "../dominio/completitud";
import {
  aparcadasDe,
  claveEmbudo,
  claveEtapa,
  columnasTablero,
  EMBUDOS,
  esActiva,
  esAparcada,
  puedeTocarTarjeta,
} from "../dominio/etapas";
import { conInteres, ordenInteres } from "../dominio/catalogos";
import type { Embudo, Etapa, Negocio } from "../dominio/tipos";
import { BotonBuscar } from "../negocios/BuscadorFichas";
import { EtiquetaWeb } from "../negocios/EtiquetaWeb";
import { MenuInteres } from "../negocios/Interes";
import { AvatarUsuario } from "../usuarios/FotoUsuario";
import { MarcaInteresado } from "../negocios/MarcaInteresado";
import {
  formatoFecha,
  formatoFechaHora,
  formatoImporte,
} from "../negocios/formato";
import { Semaforo } from "../negocios/Semaforo";
import { useAbrirFicha } from "../negocios/useAbrirFicha";
import { useMoverEtapa } from "../negocios/useMoverEtapa";
import { useFiltroEtapas } from "../datos/useFiltroEtapas";

type Columnas = Partial<Record<Etapa, Negocio[]>>;

const agrupar = (proyectos: Negocio[], etapas: Etapa[]): Columnas => {
  const columnas: Columnas = Object.fromEntries(etapas.map((e) => [e, []]));
  for (const p of proyectos) columnas[p.etapa]?.push(p);
  for (const e of etapas) {
    columnas[e]!.sort(
      (a, b) =>
        // 28d (Robert, 15/10/2026): calientes arriba, después tibios, fríos y sin nivel; dentro
        // de cada nivel, el orden que tengan. Sin la 28d nadie trae nivel y no cambia nada.
        ordenInteres(a.interes) - ordenInteres(b.interes) ||
        (a.orden ?? 9999) - (b.orden ?? 9999) ||
        (b.proyecto_updated_at ?? b.created_at).localeCompare(
          a.proyecto_updated_at ?? a.created_at,
        ),
    );
  }
  return columnas;
};

/**
 * Importe que enseña la tarjeta: en Clientes y en «Aceptada», el aceptado (39: lo ven también el
 * setter y el consultor); en el resto de Ventas, la previsión (solo Jan y Robert).
 */
const importeDe = (p: Negocio) =>
  p.embudo === "clientes" || p.etapa === "aceptada"
    ? (p.importe_total ?? p.valor_estimado)
    : p.valor_estimado;

/**
 * Pipeline (S8, Robert 02/10/2026): dos embudos, Ventas y Clientes; cada tarjeta es un
 * PROYECTO. Ventas empieza en «En conversación» (Lead nuevo vive en Leads) y termina en
 * «Cobrado»: soltar ahí pide el cobro y el proyecto pasa solo a Clientes. Soltar en una
 * etapa con fecha (consultoría, presentación, Largo plazo) la pide antes de mover.
 */
export const PipelinePage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const [embudo, setEmbudo] = useStore<Embudo>(
    "nevora.pipeline.embudo",
    "ventas",
  );
  const [mostrarAparcados, setMostrarAparcados] = useState(false);
  // S10: Jan y Robert ven todas las fichas y filtran por persona; un setter, solo lo suyo.
  const [persona, setPersona] = useFiltroPersonas();
  // 34: sin las etapas que la base aún no tiene (no se podría soltar ahí una tarjeta).
  const filtroEtapas = useFiltroEtapas();
  const etapas = useMemo(
    () => filtroEtapas([...columnasTablero(embudo), ...aparcadasDe(embudo)]),
    [embudo, filtroEtapas],
  );
  const { data, isPending, refetch } = useGetList<Negocio>("proyectos_vista", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "orden", order: "ASC" },
    filter: { embudo, en_leads: true, "etapa@in": `(${etapas.join(",")})` },
  });
  const [columnas, setColumnas] = useState<Columnas>({});
  const { mover, dialogos } = useMoverEtapa();

  // 28d: «Solo calientes / interesados» (se recuerda por usuario); solo si la base trae el nivel.
  const [soloCalientes, setSoloCalientes] = useStore<boolean>(
    "pipeline.solo_calientes",
    false,
  );
  const hayNivel = (data ?? []).some(conInteres);
  const visibles = useMemo(
    () =>
      filtrarPorPersonas(data ?? [], persona).filter(
        (p) =>
          !hayNivel ||
          !soloCalientes ||
          p.interes === "caliente" ||
          !!p.interesado_at,
      ),
    [data, persona, hayNivel, soloCalientes],
  );

  useEffect(() => {
    setColumnas(agrupar(visibles, etapas));
  }, [visibles, etapas]);

  const etapasVisibles = useMemo(
    () => (mostrarAparcados ? etapas : filtroEtapas(columnasTablero(embudo))),
    [mostrarAparcados, etapas, embudo, filtroEtapas],
  );

  const renumerar = async (lista: Negocio[], salvo?: Negocio) => {
    await Promise.all(
      lista
        .map((n, i) => ({ n, i }))
        .filter(
          ({ n, i }) =>
            n.proyecto_id !== salvo?.proyecto_id && (n.orden ?? -1) !== i,
        )
        .map(({ n, i }) => dataProvider.ordenarProyecto(n.proyecto_id, i)),
    );
  };

  const onDragEnd: OnDragEndResponder = async (resultado) => {
    const { source, destination } = resultado;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }
    const origen = source.droppableId as Etapa;
    const destino = destination.droppableId as Etapa;
    const movido = columnas[origen]?.[source.index];
    if (!movido) return;

    // Cambio local inmediato; si no se mueve (cancelado o rechazado), vuelve a su sitio.
    const nuevas: Columnas = { ...columnas };
    nuevas[origen] = [...(columnas[origen] ?? [])];
    nuevas[origen]!.splice(source.index, 1);
    nuevas[destino] =
      origen === destino ? nuevas[origen] : [...(columnas[destino] ?? [])];
    nuevas[destino]!.splice(destination.index, 0, {
      ...movido,
      etapa: destino,
    });
    setColumnas(nuevas);

    if (origen === destino) {
      try {
        await dataProvider.ordenarProyecto(
          movido.proyecto_id,
          destination.index,
        );
        await renumerar(nuevas[destino]!, movido);
      } finally {
        refetch();
      }
      return;
    }
    mover(movido, destino, {
      orden: destination.index,
      alTerminar: async (movida) => {
        if (!movida) {
          setColumnas(agrupar(visibles, etapas));
          return;
        }
        if (destino !== "cobrado") await renumerar(nuevas[destino]!, movido);
        refetch();
      },
    });
  };

  const pestanas = (
    <div
      role="tablist"
      aria-label={translate("nevora.pipeline.embudo")}
      className="inline-flex rounded-lg border bg-card p-1 gap-1"
    >
      {EMBUDOS.map((e) => (
        <button
          key={e}
          type="button"
          role="tab"
          aria-selected={embudo === e}
          onClick={() => setEmbudo(e)}
          className={cn(
            "px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
            embudo === e
              ? "bg-foreground text-background"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {translate(claveEmbudo(e))}
        </button>
      ))}
    </div>
  );

  const botonCalientes = hayNivel ? (
    <Button
      variant={soloCalientes ? "default" : "outline"}
      size="sm"
      aria-pressed={soloCalientes}
      onClick={() => setSoloCalientes(!soloCalientes)}
      data-testid="solo-calientes"
    >
      <Flame className="size-4 mr-1" />
      {translate("nevora.interes.solo_calientes")}
    </Button>
  ) : null;

  const botonAparcados = (
    <Button
      variant="outline"
      size="sm"
      onClick={() => setMostrarAparcados((v) => !v)}
    >
      {mostrarAparcados ? (
        <EyeOff className="size-4 mr-1" />
      ) : (
        <Eye className="size-4 mr-1" />
      )}
      {translate(
        mostrarAparcados
          ? "nevora.pipeline.ocultar_aparcados"
          : "nevora.pipeline.ver_aparcados",
      )}
    </Button>
  );

  // Crear una ficha directamente en el tablero (petición de Robert): nace en «En
  // conversación», la primera columna de Ventas; el alta permite cambiar la etapa.
  const botonNueva =
    embudo === "ventas" ? (
      <Button asChild size="sm">
        <Link to="/negocios/create?etapa=en_conversacion">
          <Plus className="size-4 mr-1" />
          {translate("nevora.pipeline.nueva_ficha")}
        </Link>
      </Button>
    ) : null;

  const tablero = isPending ? null : (
    <DragDropContext onDragEnd={onDragEnd}>
      <div
        // relative: lo posicionado dentro de las columnas (los textos solo para lectores de pantalla
        // del contador) queda dentro del tablero; sin esto estiraba la página y dejaba un hueco en
        // blanco a la derecha (Robert, 03/11/2026).
        className="relative flex gap-3 overflow-x-auto pb-4 -mx-4 px-4 md:mx-0 md:px-0"
        data-guia="pipeline-tablero"
      >
        {etapasVisibles.map((etapa) => (
          <Columna
            key={etapa}
            etapa={etapa}
            proyectos={columnas[etapa] ?? []}
          />
        ))}
      </div>
    </DragDropContext>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <h1 className="titulo-seccion">
            {translate("nevora.pipeline.titulo")}
          </h1>
          <div className="ml-auto flex items-center gap-1">
            <BotonBuscar />
            <CampanaAvisos plegada alinear="end" />
          </div>
        </MobileHeader>
        <MobileContent>
          <div className="mb-3 flex flex-wrap gap-2">
            {pestanas}
            {botonNueva}
            {botonAparcados}
            {botonCalientes}
            <FiltroPersonasSelect valor={persona} cambiar={setPersona} />
          </div>
          {tablero}
          {dialogos}
        </MobileContent>
      </>
    );
  }
  return (
    <div className="mt-2 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <h1 className="titulo-pagina">
            {translate("nevora.pipeline.titulo")}
          </h1>
          {pestanas}
        </div>
        <div className="flex gap-2">
          <FiltroPersonasSelect valor={persona} cambiar={setPersona} />
          {botonCalientes}
          {botonAparcados}
          {botonNueva}
        </div>
      </div>
      {tablero}
      {dialogos}
    </div>
  );
};

const Columna = ({
  etapa,
  proyectos,
}: {
  etapa: Etapa;
  proyectos: Negocio[];
}) => {
  const translate = useTranslate();
  const aparcada = esAparcada(etapa);
  const valor = proyectos.reduce((s, n) => s + (importeDe(n) ?? 0), 0);
  return (
    <div className="w-64 shrink-0 flex flex-col">
      {/* Rediseño 30/10/2026: carriles tintados sin borde; las tarjetas, blancas con sombra. */}
      <div className="px-1.5 pb-2.5">
        <h2
          className={cn(
            "flex items-center gap-2 text-[13px] font-semibold",
            aparcada ? "text-muted-foreground" : "text-foreground",
          )}
        >
          <span className="truncate">{translate(claveEtapa(etapa))}</span>
          <span className="rounded-full bg-foreground/[0.06] px-1.5 py-px text-[11px] font-medium tabular-nums text-muted-foreground">
            <span className="sr-only">· </span>
            {proyectos.length}
          </span>
        </h2>
        {!aparcada && valor > 0 ? (
          <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
            {translate("nevora.pipeline.total_valor", {
              valor: formatoImporte(valor),
            })}
          </p>
        ) : null}
      </div>
      <Droppable droppableId={etapa}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={cn(
              "flex-1 min-h-32 rounded-xl p-2 space-y-2 transition-colors",
              snapshot.isDraggingOver
                ? "bg-accent ring-1 ring-[#3D8BD4]/30"
                : "bg-foreground/[0.03] dark:bg-foreground/[0.04]",
              etapa === "cobrado" &&
                !snapshot.isDraggingOver &&
                "border border-dashed border-border",
            )}
          >
            {proyectos.length === 0 && !snapshot.isDraggingOver ? (
              <p className="text-xs text-muted-foreground text-center py-6 px-2">
                {translate(
                  etapa === "cobrado"
                    ? "nevora.pipeline.soltar_cobro"
                    : "nevora.pipeline.vacio",
                )}
              </p>
            ) : null}
            {proyectos.map((n, i) => (
              <Tarjeta key={String(n.proyecto_id)} proyecto={n} index={i} />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};

/**
 * Tarjeta del tablero (Robert, 02/10/2026): negocio, persona de contacto, lo próximo que
 * toca (consultoría, presentación o siguiente paso, en rojo si está vencido), semáforo,
 * responsable (inicial con su color), sector e importe. Al pulsarla se abre la ficha en la
 * ventana, con este proyecto en foco.
 */
const Tarjeta = ({ proyecto, index }: { proyecto: Negocio; index: number }) => {
  const translate = useTranslate();
  const abrirFicha = useAbrirFicha();
  const responsables = useResponsables();
  const esAdmin = useEsAdmin();
  const gestiona = useGestiona();
  const accion = proximaAccion(proyecto);
  const faltan = textoDatosQueFaltan(translate, proyecto);
  const importe = importeDe(proyecto);
  const textoAccion = accion
    ? `${
        accion.tipo === "siguiente_paso"
          ? accion.texto || translate("nevora.agenda.siguiente_paso")
          : translate(`nevora.agenda.${accion.tipo}`)
      } · ${accion.conHora ? formatoFechaHora(accion.fecha) : formatoFecha(accion.fecha)}`
    : null;
  return (
    <Draggable
      draggableId={String(proyecto.proyecto_id)}
      index={index}
      // S10: un setter no arrastra las tarjetas que llevan Jan y Robert (de la consultoría en adelante).
      isDragDisabled={!puedeTocarTarjeta(proyecto, gestiona(proyecto))}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...(provided.draggableProps as unknown as React.HTMLAttributes<HTMLDivElement>)}
          {...provided.dragHandleProps}
          onClick={() => abrirFicha(proyecto.negocio_id, proyecto.proyecto_id)}
          className={cn(
            "rounded-lg border border-border/50 bg-card px-3 py-2.5 cursor-pointer transition-shadow space-y-1.5",
            snapshot.isDragging
              ? "shadow-elevada rotate-1"
              : "shadow-tarjeta hover:shadow-elevada",
          )}
        >
          {/* Robert, 30/10/2026: el nombre arriba, entero; los iconos debajo y luego lo demás. */}
          <div className="flex items-start gap-2">
            <Semaforo negocio={proyecto} className="mt-1.5" />
            <span className="font-medium text-sm leading-snug line-clamp-2 break-words flex-1 min-w-0">
              {proyecto.nombre}
            </span>
            {responsables.foto(proyecto.asignado_a) ? (
              <AvatarUsuario
                nombre={responsables.nombre(proyecto.asignado_a)}
                foto={responsables.foto(proyecto.asignado_a)}
                className="size-6 text-[11px]"
              />
            ) : null}
            <span
              hidden={!!responsables.foto(proyecto.asignado_a)}
              className="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white"
              style={{
                backgroundColor: responsables.color(proyecto.asignado_a),
              }}
              title={
                responsables.nombre(proyecto.asignado_a) ||
                translate("nevora.negocios.sin_asignar")
              }
              aria-label={`${translate("nevora.negocios.responsable")}: ${
                responsables.nombre(proyecto.asignado_a) ||
                translate("nevora.negocios.sin_asignar")
              }`}
            >
              {proyecto.asignado_a
                ? responsables.inicial(proyecto.asignado_a)
                : "–"}
            </span>
          </div>
          {proyecto.origen === "web" ||
          proyecto.interesado_at ||
          conInteres(proyecto) ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {proyecto.origen === "web" ? (
                <EtiquetaWeb className="px-1.5 text-[11px]" />
              ) : null}
              <MarcaInteresado negocio={proyecto} soloIcono />
              <MenuInteres
                negocio={proyecto}
                puedeCambiar={puedeTocarTarjeta(proyecto, gestiona(proyecto))}
                soloIcono
              />
            </div>
          ) : null}
          {proyecto.proyecto_nombre ? (
            <p className="text-xs font-medium truncate">
              {proyecto.proyecto_nombre}
            </p>
          ) : null}
          {proyecto.persona_contacto ? (
            <p className="text-xs text-muted-foreground truncate">
              {proyecto.persona_contacto}
            </p>
          ) : null}
          {textoAccion ? (
            <p
              className={cn(
                "text-xs truncate",
                accion?.vencida
                  ? "font-medium text-red-700 dark:text-red-400"
                  : "text-foreground",
              )}
              title={textoAccion}
            >
              {accion?.vencida
                ? `${translate("nevora.pipeline.vencido")} · `
                : ""}
              {textoAccion}
            </p>
          ) : esActiva(proyecto.etapa) ? (
            // Auditoría 30/10/2026: todo proyecto vivo tiene una próxima acción; si no, se ve.
            <p
              className="text-xs font-medium text-amber-700 dark:text-amber-400"
              data-testid="sin-siguiente-paso"
            >
              {translate("nevora.negocios.sin_siguiente_paso")}
            </p>
          ) : null}
          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span className="truncate">
              {[
                esAdmin ? responsables.nombre(proyecto.asignado_a) : null,
                proyecto.sector,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
            <span className="flex shrink-0 items-center gap-1.5">
              {faltan ? (
                <CircleAlert
                  className="size-3.5 text-amber-700 dark:text-amber-400"
                  aria-label={faltan}
                >
                  <title>{faltan}</title>
                </CircleAlert>
              ) : null}
              {importe != null && importe > 0 ? (
                <span className="font-medium text-foreground">
                  {formatoImporte(importe)}
                </span>
              ) : null}
            </span>
          </div>
        </div>
      )}
    </Draggable>
  );
};

PipelinePage.path = "/pipeline";
